import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Ban, CheckCircle2, DollarSign, Eye, PackageCheck, Percent, Store, TrendingUp, Upload } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api } from "../lib/api";
import { setMeta } from "../lib/seo";

const STEPS = [
  { icon: Store, title: "1. Apply in minutes", text: "Tell us about your business and the affiliate networks you work with — Amazon Associates, Temu, AliExpress, CJ, Impact, ShareASale, or your own program." },
  { icon: Upload, title: "2. Submit your products", text: "Send products with your affiliate or external merchant links, images, and pricing. Every listing keeps your tracking intact — clicks and sales are 100% yours." },
  { icon: CheckCircle2, title: "3. Quality review", text: "Our team checks each product for accurate info, fair pricing, and compliance with our quality and content guidelines (usually 3–5 business days)." },
  { icon: TrendingUp, title: "4. Go live & grow", text: "Approved products appear in categories, deals, and guides. Upgrade anytime with featured listings, sponsored placements, or blog reviews." },
];

const BENEFITS = [
  { icon: Eye, title: "Increased visibility", text: "Get in front of deal-hunters actively comparing products — not passive scrollers." },
  { icon: DollarSign, title: "Zero listing fees", text: "List for free. Optional paid placements are always clearly labeled and totally optional." },
  { icon: Percent, title: "Performance-based", text: "You keep full control of commissions through your own affiliate network terms." },
  { icon: PackageCheck, title: "You stay in control", text: "Inventory, pricing, shipping, refunds, and customer support remain 100% yours." },
];

const PROHIBITED = [
  "Weapons, ammunition, or explosives",
  "Illegal drugs or drug paraphernalia",
  "Counterfeit or trademark-infringing goods",
  "Adult content or services",
  "Hazardous or recalled products",
  "Medical claims not backed by regulation (cures, treatments)",
  "Live animals",
  "Products prohibited by our affiliate network partners",
];

const FAQ = [
  { q: "Who handles inventory, shipping, and refunds?", a: "You do — always. ShoppinMagic never touches your products or your customers' orders. We are a discovery and referral platform only." },
  { q: "How do commissions work?", a: "You earn revenue from sales on your own merchant site or marketplace, through your own affiliate program terms. ShoppinMagic earns a standard referral commission from the network — never taken from your payout." },
  { q: "What makes a good product listing?", a: "Clear photos on clean backgrounds, accurate titles and pricing, at least 4 key features, and honest descriptions. Listings with verified reviews perform best." },
  { q: "Can I promote my listings?", a: "Yes — featured listings, sponsored placements, category highlights, and blog product reviews are available to approved merchants. All sponsored content is labeled." },
  { q: "How long does approval take?", a: "Most applications are reviewed within 3–5 business days. We'll email you either way." },
];

export default function SellerPage() {
  const [form, setForm] = useState({ business_name: "", contact_name: "", email: "", website: "", network: "", message: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setMeta(
      "Sell on ShoppinMagic — Merchant Onboarding",
      "List your products on ShoppinMagic with affiliate or merchant links. Zero listing fees, full control of inventory, pricing, and shipping."
    );
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name.trim() || !form.contact_name.trim()) {
      toast.error("Please add your business and contact name.");
      return;
    }
    setLoading(true);
    try {
      await api.applySeller(form);
      toast.success("Application received! We'll review and reply within 3–5 business days.");
      setForm({ business_name: "", contact_name: "", email: "", website: "", network: "", message: "" });
    } catch {
      toast.error("Couldn't submit your application. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="seller-page">
      <section className="bg-navy-900 py-16 text-center">
        <div className="mx-auto max-w-3xl px-4">
          <span className="text-xs font-bold uppercase tracking-wider text-gold-400">Merchant onboarding</span>
          <h1 className="font-heading mt-3 text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
            Your products. Our audience. Zero risk.
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-sm text-slate-300 sm:text-base">
            ShoppinMagic is an affiliate marketplace — you keep your store, your inventory, your pricing, and your
            customers. We send motivated shoppers your way through your own affiliate links.
          </p>
          <a
            href="#apply"
            data-testid="seller-hero-cta"
            className="mt-8 inline-flex items-center gap-2 rounded-xl bg-gold-600 px-8 py-3.5 text-sm font-bold text-white shadow-lg transition-colors hover:bg-gold-700"
          >
            Start onboarding <TrendingUp size={16} />
          </a>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8" data-testid="seller-steps">
        <h2 className="font-heading text-center text-2xl font-extrabold text-slate-900 sm:text-3xl">How onboarding works</h2>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map(({ icon: Icon, title, text }) => (
            <div key={title} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gold-50 text-gold-600"><Icon size={20} /></div>
              <h3 className="mt-4 font-bold text-slate-900">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white py-14" data-testid="seller-benefits">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-center text-2xl font-extrabold text-slate-900 sm:text-3xl">Why merchants partner with us</h2>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {BENEFITS.map(({ icon: Icon, title, text }) => (
              <div key={title} className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
                <Icon size={22} className="text-gold-600" />
                <h3 className="mt-3 font-bold text-slate-900">{title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-slate-600">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-8 px-4 py-14 sm:px-6 lg:grid-cols-2 lg:px-8">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8" data-testid="seller-requirements">
          <h2 className="font-heading text-xl font-bold text-slate-900 sm:text-2xl">Seller requirements & quality guidelines</h2>
          <ul className="mt-4 space-y-2.5 text-sm text-slate-600">
            {[
              "Accurate product titles, images, pricing, and availability",
              "Valid affiliate or merchant links from a recognized network",
              "Compliance with FTC guidelines and consumer protection laws",
              "Products meet applicable safety standards and certifications",
              "Responsive customer support and a published return policy",
              "No misleading claims, fake reviews, or inflated list prices",
            ].map((r) => (
              <li key={r} className="flex items-start gap-2"><CheckCircle2 size={16} className="mt-0.5 shrink-0 text-emerald-600" />{r}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl border border-gold-100 bg-gold-50 p-6 sm:p-8" data-testid="seller-prohibited">
          <h2 className="font-heading flex items-center gap-2 text-xl font-bold text-slate-900 sm:text-2xl">
            <Ban size={20} className="text-gold-600" /> Prohibited products
          </h2>
          <ul className="mt-4 space-y-2.5 text-sm text-slate-600">
            {PROHIBITED.map((p) => (
              <li key={p} className="flex items-start gap-2"><Ban size={15} className="mt-0.5 shrink-0 text-gold-500" />{p}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-4 pb-14 sm:px-6 lg:px-8" data-testid="seller-faq">
        <h2 className="font-heading text-center text-2xl font-extrabold text-slate-900 sm:text-3xl">Merchant FAQ</h2>
        <Accordion type="single" collapsible className="mt-6">
          {FAQ.map((f, i) => (
            <AccordionItem key={i} value={`seller-faq-${i}`} className="rounded-xl border border-slate-200 bg-white px-4 mb-2 shadow-sm">
              <AccordionTrigger data-testid={`seller-faq-trigger-${i}`} className="text-left text-sm font-semibold text-slate-800">{f.q}</AccordionTrigger>
              <AccordionContent className="text-sm text-slate-600">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <section id="apply" className="mx-auto max-w-3xl scroll-mt-24 px-4 pb-16 sm:px-6 lg:px-8">
        <form onSubmit={submit} data-testid="seller-application-form" className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <h2 className="font-heading text-xl font-bold text-slate-900 sm:text-2xl">Apply to become a merchant</h2>
          <p className="mt-1 text-sm text-slate-500">Free to apply. We reply within 3–5 business days.</p>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="seller-business" className="mb-1 block text-xs font-bold text-slate-700">Business name *</label>
              <input id="seller-business" value={form.business_name} onChange={set("business_name")} required data-testid="seller-business-input"
                className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:border-gold-500 focus:outline-none focus:ring-2 focus:ring-gold-200" placeholder="Acme Goods Co." />
            </div>
            <div>
              <label htmlFor="seller-contact-name" className="mb-1 block text-xs font-bold text-slate-700">Contact name *</label>
              <input id="seller-contact-name" value={form.contact_name} onChange={set("contact_name")} required data-testid="seller-contact-input"
                className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:border-gold-500 focus:outline-none focus:ring-2 focus:ring-gold-200" placeholder="Jane Smith" />
            </div>
            <div>
              <label htmlFor="seller-email" className="mb-1 block text-xs font-bold text-slate-700">Email *</label>
              <input id="seller-email" type="email" value={form.email} onChange={set("email")} required data-testid="seller-email-input"
                className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:border-gold-500 focus:outline-none focus:ring-2 focus:ring-gold-200" placeholder="jane@acmegoods.com" />
            </div>
            <div>
              <label htmlFor="seller-website" className="mb-1 block text-xs font-bold text-slate-700">Store / website URL</label>
              <input id="seller-website" value={form.website} onChange={set("website")} data-testid="seller-website-input"
                className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:border-gold-500 focus:outline-none focus:ring-2 focus:ring-gold-200" placeholder="https://…" />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="seller-network" className="mb-1 block text-xs font-bold text-slate-700">Affiliate network(s)</label>
              <input id="seller-network" value={form.network} onChange={set("network")} data-testid="seller-network-input"
                className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:border-gold-500 focus:outline-none focus:ring-2 focus:ring-gold-200" placeholder="e.g. Amazon Associates, CJ, Impact, own program" />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="seller-message" className="mb-1 block text-xs font-bold text-slate-700">Tell us about your products</label>
              <textarea id="seller-message" value={form.message} onChange={set("message")} rows={4} data-testid="seller-message-input"
                className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:border-gold-500 focus:outline-none focus:ring-2 focus:ring-gold-200" placeholder="Categories, price range, what makes your products great…" />
            </div>
          </div>
          <button type="submit" disabled={loading} data-testid="seller-submit-button"
            className="mt-6 w-full rounded-xl bg-gold-600 py-3.5 text-sm font-bold text-white shadow-sm transition-colors hover:bg-gold-700 disabled:opacity-60">
            {loading ? "Submitting…" : "Submit application"}
          </button>
          <p className="mt-3 text-center text-xs text-slate-400">
            By applying you agree to our <a href="/legal/terms-of-service" className="underline">Terms of Service</a>.
          </p>
        </form>
      </section>
    </div>
  );
}
